/**
 * Shown when Zoom refuses a host-only step. Exported so the dashboard can tell
 * that its error banner is already saying this and drop the duplicate advisory
 * it would otherwise print under the stage line.
 */
export const HOST_ONLY_STEP_MESSAGE =
  "Only the meeting host can close the round and move to the next one. Ask the host to run this step.";

/** Zoom breakout error codes worth explaining rather than dumping raw. */
const MESSAGES: Record<string, string> = {
  "10121": "The rooms are already open — someone on your team opened them. Press Refresh to catch up.",
  "10092": "The rooms are already closed — someone on your team closed them. Press Refresh to catch up.",
  "10100": "Zoom would not open the rooms. Ask the meeting host to run this step.",
  "10101": "Zoom would not close the rooms. Ask the meeting host to run this step.",
  // Zoom's message is "No Breakout Room exist." — misleading when the rooms are
  // visible in the client, so say what to do rather than repeating it.
  "10065": "The breakout rooms are no longer available to this app. Press Refresh, then create the rooms again.",
  // Zoom's own text is "The Zoom client encountered an error while processing
  // the request!", which reads like a transient glitch. It is not: measured
  // 2026-08-19 on rooms the app had just created itself, closeBreakoutRooms and
  // deleteBreakoutRoom return this for a co-host and succeed for the host,
  // minutes apart on the same rooms. Zoom documents Co-Host as supported on
  // both. The app cannot detect its own role to hide the button — Participant
  // carries no self flag and getUserContext is not on the Marketplace listing —
  // so name both working routes instead of suggesting a retry that never works.
  "10001": HOST_ONLY_STEP_MESSAGE,
};

/** True when the failure means the team already did this step — the app should re-sync, not error. */
export function isAlreadyDoneCode(code: string | null): boolean {
  return code === "10121" || code === "10092";
}

/** Pulls the Zoom status code off a rejection, whichever shape the SDK used. */
export function zoomErrorCode(err: unknown): string | null {
  const raw = (err as { code?: unknown })?.code;
  if (typeof raw === "number" || typeof raw === "string") return String(raw);
  const message = err instanceof Error ? err.message : String(err ?? "");
  return /\b(1\d{4})\b/.exec(message)?.[1] ?? null;
}

/** Friendly copy for a known code, or null to fall back to the raw failure banner. */
export function breakoutErrorMessage(err: unknown): string | null {
  const code = zoomErrorCode(err);
  return code ? MESSAGES[code] ?? null : null;
}
